<template>
	<div>
		<div class="card">
			<div class="card-header d-flex justify-content-between align-items-center">
				<div>
					<h4 class="mb-0">☁️ HeyGov + ClerkMinutes Sync</h4>
					<small>Pull HeyGov products and ClerkMinutes status into MC2. Preview first, then run.</small>
				</div>
				<div>
					<button class="btn btn-outline-primary me-2" :disabled="loading" @click="sync('preview')">
						<span v-if="loading && mode === 'preview'" class="spinner-border spinner-border-sm me-1"></span>
						Preview
					</button>
					<button class="btn btn-primary" :disabled="loading || !report" @click="openConfirm">
						<span v-if="loading && mode === 'commit'" class="spinner-border spinner-border-sm me-1"></span>
						Run sync
					</button>
				</div>
			</div>
			<div class="card-body">
				<p v-if="!report && !loading" class="text-muted">
					Click <b>Preview</b> to see what would change. Nothing is written until you click <b>Run sync</b>.
				</p>

				<div v-if="report">
					<div class="alert" :class="report.committed ? 'alert-success' : 'alert-info'">
						<b>{{ report.committed ? 'Committed' : 'Preview' }}</b> &middot;
						{{ report.changed_municipalities }} municipalities and {{ report.changed_clients }} clients
						{{ report.committed ? 'updated' : 'would change' }}.
						<span class="text-muted">({{ report.generated_at }})</span>
					</div>

					<div class="row mb-3">
						<div class="col-md-6 mb-2" v-if="report.heygov">
							<div class="card h-100">
								<div class="card-body">
									<h6 class="fw-bold">HeyGov products <span class="badge bg-secondary">{{ report.heygov.source }}</span></h6>
									<p class="m-0">Fetched: {{ report.heygov.fetched }} &middot; Matched: {{ report.heygov.matched }} &middot; Unmatched: {{ report.heygov.unmatched }}</p>
									<p v-if="report.heygov.error" class="text-danger m-0">{{ report.heygov.error }}</p>
								</div>
							</div>
						</div>
						<div class="col-md-6 mb-2" v-if="report.clerkminutes">
							<div class="card h-100">
								<div class="card-body">
									<h6 class="fw-bold">ClerkMinutes <span class="badge bg-secondary">{{ report.clerkminutes.source }}</span></h6>
									<p class="m-0">
										Fetched: {{ report.clerkminutes.fetched }} &middot;
										Matched: {{ report.clerkminutes.matched }} (unique {{ report.clerkminutes.matched_unique }}, deduped {{ report.clerkminutes.deduped || 0 }}) &middot;
										Unmatched: {{ report.clerkminutes.unmatched }}
									</p>
									<p v-if="report.clerkminutes.error" class="text-danger m-0">{{ report.clerkminutes.error }}</p>
									<p v-if="report.clerkminutes.live_error" class="text-warning m-0">{{ report.clerkminutes.live_error }}</p>
								</div>
							</div>
						</div>
					</div>

					<h6 class="fw-bold">Sample changes <small class="text-muted">(first {{ report.sample_changes.length }})</small></h6>
					<div class="table-responsive">
						<table class="table table-sm table-striped">
							<thead>
								<tr>
									<th>Municipality</th>
									<th>State</th>
									<th>Municipality changes</th>
									<th>Client changes</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="s in report.sample_changes" :key="s.municipality_id">
									<td>{{ s.name }} <span class="text-muted">#{{ s.municipality_id }}</span></td>
									<td>{{ s.state }}</td>
									<td>
										<span v-for="(pair, field) in s.muni_changes" :key="field" class="d-block small">
											<code>{{ field }}</code>: {{ pair[0] }} &rarr; <b>{{ pair[1] }}</b>
										</span>
									</td>
									<td>
										<span v-for="(pair, field) in s.client_changes" :key="field" class="d-block small">
											<code>{{ field }}</code>: {{ pair[0] }} &rarr; <b>{{ pair[1] }}</b>
										</span>
									</td>
								</tr>
								<tr v-if="!report.sample_changes.length">
									<td colspan="4" class="text-center text-muted">No changes.</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>

		<!-- Confirm modal (Bootstrap, not a SweetAlert toast) -->
		<div class="modal fade" id="heygovSyncConfirm" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Run sync?</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						This writes ClerkMinutes / HeyGov status to municipalities and clients.
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
						<button type="button" class="btn btn-primary" @click="runConfirmed">Yes, run it</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { Modal } from 'bootstrap'
import restApi from '../../api/index.js'
import { handleErrors } from '../../actions/helpers'
export default {
	name: 'HeyGovSync',
	data() {
		return {
			loading: false,
			mode: 'preview',
			report: null,
			confirmModal: null,
		}
	},
	mounted() {
		this.confirmModal = new Modal('#heygovSyncConfirm')
	},
	methods: {
		openConfirm() {
			this.confirmModal.show()
		},
		runConfirmed() {
			this.confirmModal.hide()
			this.sync('commit')
		},
		sync(mode) {
			this.mode = mode
			this.loading = true
			restApi
				.post('/heygov/sync', {}, { params: { mode } })
				.then(({ data }) => {
					this.report = data
					this.loading = false
					if (mode === 'commit') {
						this.$swal.fire({
							icon: 'success',
							title: 'Sync committed',
							text: `${data.changed_municipalities} municipalities, ${data.changed_clients} clients updated.`,
							timer: 5000,
						})
					}
				})
				.catch(error => {
					this.loading = false
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},
	},
}
</script>
